import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  bffApi,
  useUpdateBidV2Mutation,
  type PortalSourcingEventDetailDto,
  type BidV2Dto,
  type BidV2PatchDto,
} from "../../../../autogen/bff-api";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
} from "../../../../common/editing/save-coordinator";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { usePortalEventUrls } from "../usePortalEventUrls";
import { saveWithVersionConflictRetry } from "../../buyer/wizard/saveWithVersionConflictRetry";
import { PortalLayout } from "../components/PortalLayout";
import { PortalWizardShell } from "../../../../common/wizard/PortalWizardShell";
import { usePortalStepLabel, usePortalStepShortLabel } from "../../../../common/wizard/usePortalStepLabel";
import { useWizardStepNavigation } from "../../../../common/wizard/useWizardStepNavigation";
import { BidSigningHeaderBadge } from "./BidSigningHeaderBadge";
import { bidStepVisibility, bidWizardModel } from "./bidWizardSteps";
import { BidDocumentsStep } from "./steps/BidDocumentsStep";
import { BidIntroStep } from "./steps/BidIntroStep";
import { CoverLetterStep } from "./steps/CoverLetterStep";
import { ExclusionStep } from "./steps/ExclusionStep";
import { AwardCriteriaStep } from "./steps/AwardCriteriaStep";
import { MinimumRequirementsStep } from "./steps/MinimumRequirementsStep";
import { QualificationStep } from "./steps/QualificationStep";
import { ReviewStep } from "./steps/ReviewStep";
import { SupplierInfoStep } from "./steps/SupplierInfoStep";
import { isBidV2Editable } from "./publicBidEditability";

interface Props {
  eventId: string;
  bidId: string;
  bid: BidV2Dto;
  event: PortalSourcingEventDetailDto;
}

export const EditBidV2Form = ({ eventId, bidId, bid, event }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const portalUrls = usePortalEventUrls();
  const editorErrorConfig = useApiErrorEditorConfig();
  const [updateBid] = useUpdateBidV2Mutation();
  const visibility = bidStepVisibility(event);
  const stepLabel = usePortalStepLabel();
  const stepShortLabel = usePortalStepShortLabel();
  const { currentStep, navigateToStep, navPropsFor } = useWizardStepNavigation({
    model: bidWizardModel,
    visibility,
    stepPaths: portalUrls.bid.edit.go(eventId, bidId),
    stepLabel,
    beforeNavigate: async () => {
      await coordinatorRegistry.flush(bidId);
      return coordinatorRegistry.get(bidId)?.getEntityStatus() !== "error";
    },
  });

  const config = useMemo(
    () => ({
      initialState: bid,
      save: async (fields: Partial<BidV2PatchDto>) =>
        await saveWithVersionConflictRetry({
          fields,
          save: async (retryFields) =>
            await updateBid({ bidId, bidV2PatchDto: retryFields as BidV2PatchDto }).unwrap(),
          reloadLatest: async () => {
            await dispatch(bffApi.endpoints.getBidV2.initiate({ bidId }, { forceRefetch: true })).unwrap();
          },
        }),
      ...editorErrorConfig,
    }),
    [bid, bidId, dispatch, updateBid, editorErrorConfig],
  );

  useCreateEditor<BidV2Dto, BidV2PatchDto>(bidId, config);

  if (!currentStep) return <PortalLayout>{t("Loading")}</PortalLayout>;

  const renderStep = (): ReactNode => {
    switch (currentStep) {
      case "intro":
        return <BidIntroStep event={event} {...navPropsFor("intro")} />;
      case "supplier":
        return (
          <SupplierInfoStep
            entityId={bidId}
            bidId={bidId}
            event={event}
            readOnly={!isBidV2Editable(bid)}
            {...navPropsFor("supplier")}
          />
        );
      case "exclusion":
        return (
          <ExclusionStep
            entityId={bidId}
            event={event}
            readOnly={!isBidV2Editable(bid)}
            {...navPropsFor("exclusion")}
          />
        );
      case "qualification":
        return <QualificationStep bidId={bidId} event={event} {...navPropsFor("qualification")} />;
      case "minimumRequirements":
        return <MinimumRequirementsStep bidId={bidId} event={event} {...navPropsFor("minimumRequirements")} />;
      case "awardCriteria":
        return <AwardCriteriaStep bidId={bidId} event={event} {...navPropsFor("awardCriteria")} />;
      case "documents":
        return <BidDocumentsStep bidId={bidId} {...navPropsFor("documents")} />;
      case "cover":
        return <CoverLetterStep bidId={bidId} {...navPropsFor("cover")} />;
      case "review":
        return (
          <ReviewStep
            eventId={eventId}
            bidId={bidId}
            bid={bid}
            event={event}
            navigateToStep={navigateToStep}
            previousStep={navPropsFor("review").previousStep}
          />
        );
    }
  };

  return (
    <PortalLayout>
      <PortalWizardShell
        backTo={portalUrls.view.go(eventId)}
        backLabel={t("Back to the procurement")}
        title={t("Submit offer")}
        subtitle={event.title}
        underSubtitle={<BidSigningHeaderBadge bid={bid} />}
        steps={bidWizardModel.visibleSteps(visibility)}
        currentStep={currentStep}
        stepLabel={stepLabel}
        stepShortLabel={stepShortLabel}
        navigateToStep={navigateToStep}
      >
        {renderStep()}
      </PortalWizardShell>
    </PortalLayout>
  );
};
